import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useSession } from './useSession';

export type FollowKind = 'auction' | 'tenant';

interface UseFollowResult {
  /** True once we've checked the DB; false while loading initial state. */
  ready: boolean;
  /** True when the signed-in user follows this target. */
  following: boolean;
  /** True while a follow/unfollow mutation is in flight. */
  pending: boolean;
  /** Toggle follow → unfollow or vice versa. No-op when signed out. */
  toggle: () => Promise<void>;
  /** Error from the last attempted mutation, cleared on next attempt. */
  error: string | null;
}

/**
 * Module #23 chunk 2a — follow / unfollow hook.
 *
 * Backed by public.bidder_follows (PK: user_id + follow_kind + target_id).
 * For signed-out users: returns { ready: true, following: false } and
 * toggle() is a no-op (callers should redirect to sign-in instead).
 */
export function useFollow(kind: FollowKind, targetId: string | null): UseFollowResult {
  const { session } = useSession();
  const userId = session?.user?.id ?? null;
  const [following, setFollowing] = useState(false);
  const [ready, setReady] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load initial state.
  useEffect(() => {
    if (!userId || !targetId) {
      setFollowing(false);
      setReady(true);
      return;
    }
    let cancelled = false;
    setReady(false);
    void (async () => {
      const { data, error: err } = await supabase
        .from('bidder_follows')
        .select('target_id')
        .eq('user_id', userId)
        .eq('follow_kind', kind)
        .eq('target_id', targetId)
        .maybeSingle();
      if (cancelled) return;
      if (err) {
        setError(err.message);
      } else {
        setFollowing(!!data);
      }
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, kind, targetId]);

  const toggle = useCallback(async () => {
    if (!userId || !targetId) return;
    setPending(true);
    setError(null);
    try {
      if (following) {
        const { error: err } = await supabase
          .from('bidder_follows')
          .delete()
          .eq('user_id', userId)
          .eq('follow_kind', kind)
          .eq('target_id', targetId);
        if (err) throw err;
        setFollowing(false);
      } else {
        const { error: err } = await supabase
          .from('bidder_follows')
          .insert({ user_id: userId, follow_kind: kind, target_id: targetId });
        if (err) throw err;
        setFollowing(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
    }
  }, [userId, kind, targetId, following]);

  return { ready, following, pending, toggle, error };
}
